/**
 * OmegaOrganPage — L3 of the OMEGA zoom
 * (`/omega/brain/:brainId/:organId`).
 *
 * Renders one specialist's details: typed input/output, current
 * status, and the last 10 signed-log entries the organ produced.
 *
 * Auth: parent route is wrapped in `<AdminGuard>` in App.tsx.
 */

import { Navigate, useParams } from 'react-router-dom';

import { isOmegaAdmin } from '@/features/omega/adminMode';
import { Breadcrumbs } from '@/features/omega/Breadcrumbs';
import { findBrain, findOrgan, type OrganStatus } from '@/features/omega/brains';
import { recentEntriesForOrgan } from '@/features/omega/logStore';
import { cn } from '@/utils/cn';

export function OmegaOrganPage() {
  const { brainId, organId } = useParams<{ brainId: string; organId: string }>();

  if (!isOmegaAdmin()) {
    return <Navigate to="/" replace />;
  }

  const brain = findBrain(brainId);
  const organ = findOrgan(brainId, organId);

  if (!brain || !organ) {
    return (
      <div className="flex flex-col gap-4">
        <Breadcrumbs
          crumbs={
            brain
              ? [
                  { label: 'OMEGA', to: '/omega' },
                  { label: brain.name, to: `/omega/brain/${brain.id}` },
                  { label: 'Unknown organ' },
                ]
              : [{ label: 'OMEGA', to: '/omega' }, { label: 'Unknown' }]
          }
        />
        <p className="text-sm text-ink-muted">
          No organ registered with id <span className="font-mono">{organId}</span>{' '}
          under brain <span className="font-mono">{brainId}</span>.
        </p>
      </div>
    );
  }

  const recent = recentEntriesForOrgan(organ.id, 10);

  return (
    <div className="flex flex-col gap-6">
      <Breadcrumbs
        crumbs={[
          { label: 'OMEGA', to: '/omega' },
          { label: brain.name, to: `/omega/brain/${brain.id}` },
          { label: organ.name },
        ]}
      />

      <header className="flex flex-col gap-2">
        <div className="flex items-center gap-3">
          <h1 className="text-2xl font-semibold tracking-tight text-ink">{organ.name}</h1>
          <OrganStatusChip status={organ.status} />
        </div>
        <p className="text-sm text-ink-muted">{organ.description}</p>
      </header>

      <section className="grid grid-cols-1 gap-4 sm:grid-cols-3">
        <Field label="Input type" value={organ.input_type} />
        <Field label="Output type" value={organ.output_type} mono />
        <Field label="Last action" value={organ.last_action} />
      </section>

      <section
        aria-labelledby="organ-recent-heading"
        className="rounded-xl border border-border bg-surface p-6"
      >
        <h2 id="organ-recent-heading" className="text-lg font-semibold text-ink">
          Last 10 actions
        </h2>
        <p className="mt-1 text-sm text-ink-muted">
          Newest first. Each row is a signed log entry produced by this organ.
        </p>

        {recent.length === 0 ? (
          <p className="mt-5 rounded-md border border-dashed border-border bg-canvas p-4 text-sm text-ink-muted">
            No log entries yet for this organ.
          </p>
        ) : (
          <div className="mt-5 overflow-hidden rounded-md border border-border">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-raised text-[11px] uppercase tracking-wider text-ink-subtle">
                  <th className="px-3 py-2 text-start font-medium">Seq</th>
                  <th className="px-3 py-2 text-start font-medium">Type</th>
                  <th className="px-3 py-2 text-start font-medium">Hash</th>
                  <th className="px-3 py-2 text-start font-medium">Timestamp</th>
                </tr>
              </thead>
              <tbody>
                {recent.map((e) => (
                  <tr key={e.seq} className="border-t border-border">
                    <td className="px-3 py-2 font-mono text-xs text-ink-muted">{e.seq}</td>
                    <td className="px-3 py-2 font-mono text-xs text-ink">{e.type}</td>
                    <td className="px-3 py-2 font-mono text-xs text-ink-muted">
                      {e.hash.slice(0, 10)}…{e.hash.slice(-6)}
                    </td>
                    <td className="px-3 py-2 text-xs text-ink-muted">
                      {new Date(e.timestamp).toLocaleString()}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </section>
    </div>
  );
}

function Field({ label, value, mono }: { label: string; value: string; mono?: boolean }) {
  return (
    <div className="flex flex-col gap-1 rounded-lg border border-border bg-canvas px-3 py-3">
      <span className="text-[10px] uppercase tracking-wider text-ink-subtle">{label}</span>
      <span className={cn('text-sm font-medium text-ink', mono && 'font-mono text-xs')}>
        {value}
      </span>
    </div>
  );
}

function OrganStatusChip({ status }: { status: OrganStatus }) {
  const tone: Record<OrganStatus, string> = {
    active: 'border-success/30 bg-success-soft text-success',
    idle: 'border-border bg-surface-raised text-ink-muted',
    error: 'border-danger/30 bg-danger/10 text-danger',
  };
  return (
    <span
      className={cn(
        'inline-flex items-center rounded-full border px-2 py-0.5 text-[11px] font-medium uppercase tracking-wider',
        tone[status],
      )}
    >
      {status}
    </span>
  );
}

export default OmegaOrganPage;
